import type { ComputedRef, WritableComputedRef } from "vue";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import type { PumpData } from "@/libs/calculations/pumps/pumpData";
import type { ToleranceData } from "@/libs/calculations/pumps/toleranceData";
import type { SealData } from "@/libs/calculations/seals";
import type {
  DeviationSurveyEntryRows,
  IndividualSealCalculations,
  MotorData
} from "../calculationTypes";
import type { ScenarioCalcInputs } from "./inputs";
import type { EquipmentOrderingCalcs } from "./equipmentOrdering";
import type { LengthsAndDepthsCalcs } from "./lengthsAndDepths";
import type { DiametersAndWeightsCalcs } from "./diametersAndWeights";
import type { WellInputsCalcs } from "./wellInputs";
import type { FluidPropertiesCalcs } from "./fluidProperties";
import type { IntakeConditionsCalcs } from "./intakeConditions";
import type { PumpHydraulicsCalcs } from "./pumpHydraulics";
import type { PumpLimitsCalcs } from "./pumpLimits";
import type { FrictionCalcs } from "./friction";
import type { MotorCalcs } from "./motor";
import type { SealsCalcs } from "./seals";
import type { CablesCalcs } from "./cables";
import type { SurfaceDriveCalcs } from "./surfaceDrive";

// The calcs form one lazy graph that has cycles across domains (pump data
// needs operating RPM from the motor, the motor needs pump load, ...).
// Each domain is defined in order and receives everything the earlier
// domains defined. Values from a *later* domain are read as `ctx.name`
// inside computed getters, and are typed here by hand. Inferring them would
// make the domain types circular.
export type ForwardRefs = {
  basicData: ComputedRef<BasicData>;
  entryRows: ComputedRef<DeviationSurveyEntryRows>;
  casingInteriorDiameterAtSealsIn: ComputedRef<number>;
  datumPointFt: WritableComputedRef<number>;
  totalCablesWeightPounds: ComputedRef<number>;
  /* eslint-disable @typescript-eslint/no-explicit-any -- inferred as any from the generated GraphQL types */
  pumpIntakePressurePSIA: ComputedRef<any>;
  requiredDischargePressurePSIA: ComputedRef<any>;
  surfaceDriveMaxHz: ComputedRef<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  pumpData: ComputedRef<PumpData>;
  toleranceData: ComputedRef<ToleranceData>;
  totalRequiredBrakeHP: ComputedRef<number>;
  motorSkinTemperatureF: ComputedRef<number>;
  rpmCorrectionForPumpCalculations: ComputedRef<number>;
  operatingRPM: ComputedRef<number>;
  averageViscosityCP: ComputedRef<number>;
  averageLiquidSpecificGravity: ComputedRef<number>;
  primarySealBearingData: ComputedRef<SealData>;
  motorData: ComputedRef<MotorData>;
  individualSealCalculations: ComputedRef<IndividualSealCalculations>;
};

export type EquipmentOrderingCtx = ScenarioCalcInputs & ForwardRefs;
export type LengthsAndDepthsCtx = EquipmentOrderingCtx & EquipmentOrderingCalcs;
export type DiametersAndWeightsCtx = LengthsAndDepthsCtx &
  LengthsAndDepthsCalcs;
export type WellInputsCtx = DiametersAndWeightsCtx & DiametersAndWeightsCalcs;
export type FluidPropertiesCtx = WellInputsCtx & WellInputsCalcs;
export type IntakeConditionsCtx = FluidPropertiesCtx & FluidPropertiesCalcs;
export type PumpHydraulicsCtx = IntakeConditionsCtx & IntakeConditionsCalcs;
export type PumpLimitsCtx = PumpHydraulicsCtx & PumpHydraulicsCalcs;
export type FrictionCtx = PumpLimitsCtx & PumpLimitsCalcs;
export type MotorCtx = FrictionCtx & FrictionCalcs;
export type SealsCtx = MotorCtx & MotorCalcs;
export type CablesCtx = SealsCtx & SealsCalcs;
export type SurfaceDriveCtx = CablesCtx & CablesCalcs;
export type ScenarioCalcContext = SurfaceDriveCtx & SurfaceDriveCalcs;

// Compile-time check that every hand-typed ForwardRefs entry matches the
// type its domain actually defines.
type DefinedCalcs = WellInputsCalcs &
  IntakeConditionsCalcs &
  PumpHydraulicsCalcs &
  PumpLimitsCalcs &
  MotorCalcs &
  SealsCalcs &
  CablesCalcs &
  SurfaceDriveCalcs;
type Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
type Expect<T extends true> = T;
export type ForwardRefsMatchDefinitions = Expect<
  Equals<Pick<DefinedCalcs, keyof ForwardRefs>, ForwardRefs>
>;

// The ForwardRefs names, for the runtime check in index.ts. The type below
// fails to compile if this list and ForwardRefs ever differ.
export const FORWARD_REF_NAMES = [
  "basicData",
  "entryRows",
  "casingInteriorDiameterAtSealsIn",
  "datumPointFt",
  "totalCablesWeightPounds",
  "pumpIntakePressurePSIA",
  "requiredDischargePressurePSIA",
  "surfaceDriveMaxHz",
  "pumpData",
  "toleranceData",
  "totalRequiredBrakeHP",
  "motorSkinTemperatureF",
  "rpmCorrectionForPumpCalculations",
  "operatingRPM",
  "averageViscosityCP",
  "averageLiquidSpecificGravity",
  "primarySealBearingData",
  "motorData",
  "individualSealCalculations"
] as const;
export type ForwardRefNamesMatch = Expect<
  Equals<(typeof FORWARD_REF_NAMES)[number], keyof ForwardRefs>
>;
